import { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import type {
  DayOfWeekName,
  DesktopNotificationScheduleDto,
  DesktopNotificationType,
} from '@/api/types/desktopNotification.types';
import {
  DAY_ORDER,
  DAY_SHORT_NAMES,
  SCHEDULABLE_DAY_ORDER,
  formatScheduleSummary,
} from '@/utils/scheduleSummary';

interface ScheduleEditorValue {
  schedules: DesktopNotificationScheduleDto[];
  timeZone: string;
}

interface ScheduleEditorProps {
  schedules: DesktopNotificationScheduleDto[];
  timeZone: string;
  type: DesktopNotificationType | null;
  onChange: (next: Partial<ScheduleEditorValue>) => void;
}

const TIME_ZONES: string[] = [
  'Europe/London',
  'Europe/Berlin',
  'Europe/Warsaw',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Asia/Dubai',
  'Australia/Sydney',
  'UTC',
];

function toTimeInputValue(time: string): string {
  if (!time) return '';
  return time.length >= 5 ? time.slice(0, 5) : time;
}

function toBackendTime(value: string): string {
  if (!value) return '';
  return value.length === 5 ? `${value}:00` : value;
}

export function ScheduleEditor({ schedules, timeZone, type, onChange }: ScheduleEditorProps) {
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);

  // The generation anchor is a happiness-survey concept, and the server rejects the flag on any other type.
  const showGenerationAnchor = type === 'HappinessSurvey';

  const orderIndex = (d: DayOfWeekName) => DAY_ORDER.indexOf(d);
  const orderedSchedules = [...schedules].sort(
    (a, b) => orderIndex(a.dayOfWeek) - orderIndex(b.dayOfWeek),
  );
  const usedDays = new Set(schedules.map((s) => s.dayOfWeek));
  const availableDays = SCHEDULABLE_DAY_ORDER.filter((d) => !usedDays.has(d));

  const updateRow = (day: DayOfWeekName, patch: Partial<DesktopNotificationScheduleDto>) => {
    onChange({
      schedules: schedules.map((s) => (s.dayOfWeek === day ? { ...s, ...patch } : s)),
    });
  };

  const removeDay = (day: DayOfWeekName) => {
    onChange({ schedules: schedules.filter((s) => s.dayOfWeek !== day) });
  };

  const addDay = (day: DayOfWeekName) => {
    onChange({
      schedules: [
        ...schedules,
        {
          dayOfWeek: day,
          timeOfDay: '09:00:00',
          snoozeDurationMinutes: null,
          isGenerationAnchor: false,
        },
      ],
    });
    setAddMenuAnchor(null);
  };

  // Rewriting every row rather than patching two keeps the one-anchor rule true by construction, so
  // there is no window in which the previous and the new anchor are both set.
  const setGenerationAnchor = (day: DayOfWeekName) => {
    onChange({
      schedules: schedules.map((s) => ({ ...s, isGenerationAnchor: s.dayOfWeek === day })),
    });
  };

  const handleTimeChange = (day: DayOfWeekName) => (e: React.ChangeEvent<HTMLInputElement>) => {
    updateRow(day, { timeOfDay: toBackendTime(e.target.value) });
  };

  const handleSnoozeChange = (day: DayOfWeekName) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    if (raw === '') {
      updateRow(day, { snoozeDurationMinutes: null });
      return;
    }
    const parsed = parseInt(raw, 10);
    updateRow(day, { snoozeDurationMinutes: Number.isFinite(parsed) ? parsed : null });
  };

  const handleTimeZoneChange = (e: SelectChangeEvent<string>) => {
    onChange({ timeZone: e.target.value });
  };

  const summary = formatScheduleSummary(schedules);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          Frequency
        </Typography>
        <Chip
          label="Weekly"
          size="small"
          variant="outlined"
          color="secondary"
          aria-label="Frequency"
        />
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Days, times and snooze — leave snooze empty for no snooze on that day
          {showGenerationAnchor
            ? '. Mark the one day the survey is generated on; the rest are reminder-only'
            : ''}
        </Typography>
        <Stack spacing={1.5} role="group" aria-label="Schedule days">
          {orderedSchedules.map((row) => (
            <Stack
              key={row.dayOfWeek}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ alignItems: { sm: 'center' } }}
            >
              <Typography sx={{ minWidth: 48, fontWeight: 500 }}>
                {DAY_SHORT_NAMES[row.dayOfWeek]}
              </Typography>
              <TextField
                label="Time"
                type="time"
                size="small"
                value={toTimeInputValue(row.timeOfDay)}
                onChange={handleTimeChange(row.dayOfWeek)}
                inputProps={{ 'aria-label': `Time for ${row.dayOfWeek}` }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Snooze (min)"
                type="number"
                size="small"
                value={row.snoozeDurationMinutes ?? ''}
                onChange={handleSnoozeChange(row.dayOfWeek)}
                inputProps={{ min: 1, step: 1, 'aria-label': `Snooze minutes for ${row.dayOfWeek}` }}
                sx={{ maxWidth: 180 }}
              />
              {showGenerationAnchor && (
                <FormControlLabel
                  control={
                    <Radio
                      size="small"
                      name="schedule-generation-anchor"
                      checked={row.isGenerationAnchor}
                      onChange={() => setGenerationAnchor(row.dayOfWeek)}
                      slotProps={{
                        input: { 'aria-label': `Generation day for ${row.dayOfWeek}` },
                      }}
                    />
                  }
                  label="Generation day"
                  sx={{ mr: 0, whiteSpace: 'nowrap' }}
                />
              )}
              <IconButton
                size="small"
                onClick={() => removeDay(row.dayOfWeek)}
                aria-label={`Remove ${row.dayOfWeek}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={(e) => setAddMenuAnchor(e.currentTarget)}
          disabled={availableDays.length === 0}
          sx={{ mt: 1 }}
        >
          Add day
        </Button>
        <Menu
          anchorEl={addMenuAnchor}
          open={Boolean(addMenuAnchor)}
          onClose={() => setAddMenuAnchor(null)}
        >
          {availableDays.map((day) => (
            <MenuItem key={day} onClick={() => addDay(day)}>
              {day}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <Box>
        <Typography
          id="schedule-timezone-label"
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 0.5 }}
        >
          Time zone
        </Typography>
        <Select
          labelId="schedule-timezone-label"
          size="small"
          value={TIME_ZONES.includes(timeZone) ? timeZone : ''}
          displayEmpty
          onChange={handleTimeZoneChange}
          inputProps={{ 'aria-label': 'Time zone' }}
          sx={{ minWidth: 220 }}
        >
          {TIME_ZONES.map((tz) => (
            <MenuItem key={tz} value={tz}>
              {tz}
            </MenuItem>
          ))}
        </Select>
      </Box>

      <Typography variant="caption" color="text.secondary" data-testid="schedule-summary">
        {summary}
      </Typography>
    </Stack>
  );
}

export default ScheduleEditor;
